import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS, type MandateSummaryView } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../features/auth/auth-context';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { paths, routeTo } from '../../../routes/paths';
import styles from '../AppPages.module.css';

function isPendingAuthorization(status: string): boolean {
  return (
    status === 'PENDING_ACTIVATION_PAYMENT' ||
    status === 'PENDING_PROVIDER_AUTHORIZATION' ||
    status === 'CREATING_ORDER' ||
    status === 'ORDER_CREATED' ||
    status === 'CREATING_MANDATE'
  );
}

function formatTimestamp(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

function buildTimeline(mandate: MandateSummaryView): { label: string; at: string | null }[] {
  return [
    { label: 'Mandate created', at: mandate.createdAt },
    { label: 'Activation payment declared', at: mandate.authorizationPaymentDeclaredAt },
    { label: 'Last status check', at: mandate.lastStatusCheckAt },
    { label: 'Activated', at: mandate.activatedAt },
    { label: 'Failed', at: mandate.failedAt },
    { label: 'Expired', at: mandate.expiredAt },
    { label: 'Cancelled', at: mandate.cancelledAt },
  ];
}

export function MandateDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [cancelReason, setCancelReason] = useState('');

  const mandate = useQuery({
    queryKey: ['customer', 'mandates', id],
    queryFn: () => mandatesApi.get(id),
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['customer', 'mandates', id] });
  };

  const checkStatus = useMutation({
    mutationFn: () => mandatesApi.checkStatus(id),
    onSuccess: invalidate,
  });

  const declarePayment = useMutation({
    mutationFn: () => mandatesApi.declareActivationPayment(id),
    onSuccess: invalidate,
  });

  const requestCancellation = useMutation({
    mutationFn: () => mandatesApi.requestCancellation(id, cancelReason),
    onSuccess: async () => {
      setCancelReason('');
      await invalidate();
    },
  });

  if (mandate.isPending) {
    return <PageSpinner label="Loading mandate" />;
  }

  if (mandate.isError) {
    return <Alert tone="error">{toDisplayMessage(mandate.error)}</Alert>;
  }

  const data = mandate.data;
  const instructions = data.activationInstructions;
  const pending = isPendingAuthorization(data.internalStatus);
  const canCheckStatus = can(PERMISSIONS.MANDATES_CHECK_STATUS);
  const canDeclarePayment = can(PERMISSIONS.MANDATES_CREATE);
  const canRequestCancel = can(PERMISSIONS.MANDATES_REQUEST_CANCEL);
  const mutationError =
    checkStatus.error ?? declarePayment.error ?? requestCancellation.error ?? null;
  const mutationSuccess =
    checkStatus.isSuccess || declarePayment.isSuccess || requestCancellation.isSuccess;

  const showActivationInstructions =
    pending && instructions !== undefined && data.internalStatus !== 'FAILED';

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Direct Debit mandate</h1>
        <StatusBadge status={data.internalStatus} />
      </header>

      <p>
        <Link to={routeTo.customerContract(data.subscriptionId)}>Back to contract</Link>
      </p>

      {mutationError !== null && <Alert tone="error">{toDisplayMessage(mutationError)}</Alert>}
      {mutationSuccess && <Alert tone="success">Mandate updated.</Alert>}

      {data.internalStatus === 'ACTIVE' && (
        <Alert tone="success">
          Your Direct Debit mandate is active. Monthly instalments will be collected through this
          mandate once billing begins.
        </Alert>
      )}

      {data.internalStatus === 'FAILED' && (
        <Alert tone="error" title="Mandate activation failed">
          {data.failureReason ?? 'The payment provider could not activate this mandate.'} This
          terminal mandate cannot be retried in the portal. Confirm that your saved bank account is
          correct, then contact support so a safe replacement can be arranged.{' '}
          <Link to={paths.customerBankAccounts}>Review bank accounts</Link>
        </Alert>
      )}

      {(data.internalStatus === 'EXPIRED' || data.internalStatus === 'CANCELLED') && (
        <Alert tone="warning" title="This mandate is no longer active">
          It cannot collect repayments or be restarted from this page. Contact support if you need a
          replacement mandate.
        </Alert>
      )}

      {showActivationInstructions && (
        <section className={styles.card} aria-labelledby="activation-heading">
          <h2 className={styles.cardTitle} id="activation-heading">
            Activation instructions
          </h2>

          <p>
            Your Direct Debit mandate has been created. To activate it, transfer the displayed
            activation amount from the same bank account you registered. Your bank or the payment
            provider will verify the payment.
          </p>
          <p>
            Authorization may take approximately {instructions.expectedAuthorizationHoursMin}–
            {instructions.expectedAuthorizationHoursMax} hours. We will check the status
            automatically and notify you by email when it becomes active.
          </p>

          <dl className={styles.definitionList}>
            <dt>Activation amount</dt>
            <dd>{formatNgn(instructions.activationAmount)}</dd>
            <dt>Activation bank</dt>
            <dd>{instructions.activationBankName}</dd>
            <dt>Recipient account</dt>
            <dd>
              {instructions.activationRecipientAccount ??
                instructions.activationRecipientAccountMasked}
            </dd>
          </dl>

          <p className={styles.pageSubtitle}>
            Transfer from the same registered bank account. Only masked values are shown after
            authorization completes.
          </p>
        </section>
      )}

      <section className={styles.card} aria-labelledby="mandate-details-heading">
        <h2 className={styles.cardTitle} id="mandate-details-heading">
          Mandate details
        </h2>

        <dl className={styles.definitionList}>
          <dt>Provider status</dt>
          <dd>{data.providerStatus ?? '—'}</dd>
          <dt>Activation amount</dt>
          <dd>{data.activationAmount === null ? '—' : formatNgn(data.activationAmount)}</dd>
          <dt>Activation bank</dt>
          <dd>{data.activationBankName ?? '—'}</dd>
          <dt>Activation account</dt>
          <dd>{data.activationRecipientAccountMasked ?? '—'}</dd>
          <dt>Created</dt>
          <dd>{formatTimestamp(data.createdAt)}</dd>
          <dt>Last status check</dt>
          <dd>{formatTimestamp(data.lastStatusCheckAt)}</dd>
          <dt>Next expected check</dt>
          <dd>{formatTimestamp(data.nextStatusCheckAt)}</dd>
          <dt>Status checks</dt>
          <dd>{data.statusCheckCount}</dd>
        </dl>
      </section>

      {pending && (
        <section className={styles.card} aria-labelledby="mandate-actions-heading">
          <h2 className={styles.cardTitle} id="mandate-actions-heading">
            Actions
          </h2>

          <div className={styles.actions}>
            {canDeclarePayment && (
              <Button disabled={declarePayment.isPending} onClick={() => declarePayment.mutate()}>
                I have made the activation payment
              </Button>
            )}
            {canCheckStatus && (
              <Button
                variant="secondary"
                disabled={checkStatus.isPending}
                onClick={() => checkStatus.mutate()}
              >
                Check status
              </Button>
            )}
          </div>

          {!canDeclarePayment && !canCheckStatus && (
            <p className={styles.empty}>No actions available for your account.</p>
          )}
        </section>
      )}

      {(data.internalStatus === 'ACTIVE' ||
        data.internalStatus === 'PENDING_PROVIDER_AUTHORIZATION' ||
        data.internalStatus === 'CANCELLATION_REQUESTED') &&
        canRequestCancel && (
          <section className={styles.card} aria-labelledby="cancellation-heading">
            <h2 className={styles.cardTitle} id="cancellation-heading">
              Request cancellation
            </h2>

            <p className={styles.pageSubtitle}>
              Mandate cancellation is processed manually through the payment provider. Submit a
              request and our team will follow up.
            </p>

            <FormField
              label="Reason for cancellation"
              name="cancelReason"
              value={cancelReason}
              required
              onChange={(event) => {
                setCancelReason(event.target.value);
              }}
            />

            <div className={styles.actions}>
              <Button
                variant="secondary"
                disabled={requestCancellation.isPending || cancelReason.trim() === ''}
                onClick={() => requestCancellation.mutate()}
              >
                Submit cancellation request
              </Button>
            </div>
          </section>
        )}

      <section className={styles.card} aria-labelledby="timeline-heading">
        <h2 className={styles.cardTitle} id="timeline-heading">
          Timeline
        </h2>

        <ol className={styles.stepList}>
          {buildTimeline(data)
            .filter((entry) => entry.at !== null)
            .map((entry) => (
              <li key={entry.label}>
                <strong>{entry.label}</strong> — {formatTimestamp(entry.at)}
              </li>
            ))}
        </ol>

        {buildTimeline(data).every((entry) => entry.at === null) && (
          <p className={styles.empty}>No timeline events recorded yet.</p>
        )}
      </section>
    </div>
  );
}
